import { apiClient } from './client';
import type {
  AdminSaveStudentPayload,
  AdminUpdateUserPayload,
  AdminUser,
  AppRole,
  CreateUserPayload,
  MessageResponse,
  GroupOption,
  GroupResponse,
  ParentOption,
  SaveGroupPayload,
  SaveStudentPayload,
  StudentChange,
  StudentResponse,
  NewsListItem,
  NewsDetail,
  SaveNewsPayload,
  ContractTemplate,
  CoverUpload,
  EmailOutboxEntry,
  MessengerChat,
  OutboxEntry,
  RelayStatus,
  ContentPage,
  ContentPageListItem,
  ContentPageSlug,
  SaveContentPagePayload,
  SaveStudioRequisitesPayload,
  StudioRequisites,
  CreateSubscriptionPayload,
  MySubscriptions,
  PaymentFilters,
  PaymentRow,
  PaymentStart,
  PaymentsReport,
  MonthReport,
  MonthReportFilters,
  ReportFilters,
  CreateServiceOrderPayload,
  SaveServicePayload,
  ServiceOrder,
  ServiceOrderFilters,
  ServiceResponse,
  ServiceTile,
  ServicesReport,
  ServicesReportFilters,
  SubscriptionFilters,
  SubscriptionRegistry,
  SubscriptionOption,
  SubscriptionResponse,
} from './types';

export const groupsApi = {
  async list(includeInactive: boolean): Promise<GroupResponse[]> {
    const { data } = await apiClient.get<GroupResponse[]>('/groups', {
      params: { includeInactive },
    });
    return data;
  },

  /** Доступно и родителю: нужен для выбора группы в форме ученика. */
  async options(): Promise<GroupOption[]> {
    const { data } = await apiClient.get<GroupOption[]>('/groups/options');
    return data;
  },

  async create(payload: SaveGroupPayload): Promise<GroupResponse> {
    const { data } = await apiClient.post<GroupResponse>('/groups', payload);
    return data;
  },

  async update(id: string, payload: SaveGroupPayload): Promise<GroupResponse> {
    const { data } = await apiClient.put<GroupResponse>(`/groups/${id}`, payload);
    return data;
  },
};

export interface StudentFilters {
  groupId?: string | undefined;
  query?: string | undefined;
  includeInactive?: boolean | undefined;
}

export const studentsApi = {
  async my(): Promise<StudentResponse[]> {
    const { data } = await apiClient.get<StudentResponse[]>('/students/my');
    return data;
  },

  async list(filters: StudentFilters): Promise<StudentResponse[]> {
    const { data } = await apiClient.get<StudentResponse[]>('/students', { params: filters });
    return data;
  },

  async create(payload: SaveStudentPayload): Promise<StudentResponse> {
    const { data } = await apiClient.post<StudentResponse>('/students', payload);
    return data;
  },

  async update(id: string, payload: SaveStudentPayload): Promise<StudentResponse> {
    const { data } = await apiClient.put<StudentResponse>(`/students/${id}`, payload);
    return data;
  },

  async adminCreate(payload: AdminSaveStudentPayload): Promise<StudentResponse> {
    const { data } = await apiClient.post<StudentResponse>('/students/admin', payload);
    return data;
  },

  async adminUpdate(id: string, payload: AdminSaveStudentPayload): Promise<StudentResponse> {
    const { data } = await apiClient.put<StudentResponse>(`/students/${id}/admin`, payload);
    return data;
  },

  async history(id: string): Promise<StudentChange[]> {
    const { data } = await apiClient.get<StudentChange[]>(`/students/${id}/history`);
    return data;
  },
};

export interface UserFilters {
  query?: string | undefined;
  role?: AppRole | undefined;
  includeInactive?: boolean | undefined;
}

export const usersApi = {
  async list(filters: UserFilters): Promise<AdminUser[]> {
    const { data } = await apiClient.get<AdminUser[]>('/users', { params: filters });
    return data;
  },

  /** Адрес такой записи подтверждать не нужно: её завёл администратор. */
  async create(payload: CreateUserPayload): Promise<AdminUser> {
    const { data } = await apiClient.post<AdminUser>('/users', payload);
    return data;
  },

  async update(id: string, payload: AdminUpdateUserPayload): Promise<AdminUser> {
    const { data } = await apiClient.put<AdminUser>(`/users/${id}`, payload);
    return data;
  },

  /** Мягкая блокировка: данные остаются, вход прекращается. */
  async setActive(id: string, isActive: boolean): Promise<AdminUser> {
    const { data } = await apiClient.post<AdminUser>(`/users/${id}/active`, { isActive });
    return data;
  },

  async setAdmin(id: string, isAdmin: boolean): Promise<AdminUser> {
    const { data } = await apiClient.post<AdminUser>(`/users/${id}/admin`, { isAdmin });
    return data;
  },

  /** Выслать ссылку подтверждения заново — когда письмо с регистрации потерялось. */
  async sendConfirmation(id: string): Promise<MessageResponse> {
    const { data } = await apiClient.post<MessageResponse>(`/users/${id}/confirmation`);
    return data;
  },

  async remove(id: string): Promise<void> {
    await apiClient.delete(`/users/${id}`);
  },
};

export const parentsApi = {
  async search(query: string): Promise<ParentOption[]> {
    const { data } = await apiClient.get<ParentOption[]>('/parents', { params: { query } });
    return data;
  },
};

export const newsApi = {
  async feed(): Promise<NewsListItem[]> {
    const { data } = await apiClient.get<NewsListItem[]>('/news');
    return data;
  },

  async all(): Promise<NewsListItem[]> {
    const { data } = await apiClient.get<NewsListItem[]>('/news/all');
    return data;
  },

  async bySlug(slug: string): Promise<NewsDetail> {
    const { data } = await apiClient.get<NewsDetail>(`/news/${slug}`);
    return data;
  },

  async create(payload: SaveNewsPayload): Promise<NewsDetail> {
    const { data } = await apiClient.post<NewsDetail>('/news', payload);
    return data;
  },

  async update(id: string, payload: SaveNewsPayload): Promise<NewsDetail> {
    const { data } = await apiClient.put<NewsDetail>(`/news/${id}`, payload);
    return data;
  },

  async remove(id: string): Promise<void> {
    await apiClient.delete(`/news/${id}`);
  },

  async uploadCover(file: File): Promise<CoverUpload> {
    const form = new FormData();
    form.append('file', file);

    // У клиента по умолчанию стоит application/json. Для файла его нужно
    // снять, иначе браузер не допишет boundary и сервер не разберёт форму.
    const { data } = await apiClient.post<CoverUpload>('/news/cover', form, {
      transformRequest: [
        (payload: unknown, headers) => {
          headers.delete('Content-Type');
          return payload;
        },
      ],
    });
    return data;
  },

  async outbox(): Promise<OutboxEntry[]> {
    const { data } = await apiClient.get<OutboxEntry[]>('/news/outbox');
    return data;
  },

  /** Вернуть сообщение в очередь: адрес чата сервер пересчитает заново. */
  async retryOutbox(id: string): Promise<void> {
    await apiClient.post(`/news/outbox/${id}/retry`);
  },
};

export const messengerChatsApi = {
  async list(): Promise<MessengerChat[]> {
    const { data } = await apiClient.get<MessengerChat[]>('/messenger-chats');
    return data;
  },

  async relayStatus(): Promise<RelayStatus> {
    const { data } = await apiClient.get<RelayStatus>('/messenger-chats/relay-status');
    return data;
  },
};

export const subscriptionsApi = {
  /** Плитки страницы «Оплата»: ученики и состояние ближайших месяцев. */
  async options(): Promise<SubscriptionOption[]> {
    const { data } = await apiClient.get<SubscriptionOption[]>('/subscriptions/options');
    return data;
  },

  async my(studentId?: string): Promise<MySubscriptions> {
    const { data } = await apiClient.get<MySubscriptions>('/subscriptions/my', {
      params: studentId === undefined ? {} : { studentId },
    });
    return data;
  },

  /** Реестр администратора: абонементы плюс месяцы, за которые их не оформили. */
  async list(filters: SubscriptionFilters): Promise<SubscriptionRegistry> {
    const { data } = await apiClient.get<SubscriptionRegistry>('/subscriptions', {
      params: filters,
    });
    return data;
  },

  async get(id: string): Promise<SubscriptionResponse> {
    const { data } = await apiClient.get<SubscriptionResponse>(`/subscriptions/${id}`);
    return data;
  },

  async create(payload: CreateSubscriptionPayload): Promise<SubscriptionResponse> {
    const { data } = await apiClient.post<SubscriptionResponse>('/subscriptions', payload);
    return data;
  },

  async cancel(id: string): Promise<SubscriptionResponse> {
    const { data } = await apiClient.post<SubscriptionResponse>(`/subscriptions/${id}/cancel`);
    return data;
  },

  /**
   * Начинает оплату картой. Повторный вызов возвращает ту же форму:
   * двойное нажатие не должно превращаться в два списания.
   */
  async pay(id: string): Promise<PaymentStart> {
    const { data } = await apiClient.post<PaymentStart>(`/subscriptions/${id}/pay`);
    return data;
  },

  /**
   * Оплата вне сайта. Форма расчёта попадает в квитанцию, поэтому её
   * выбирает администратор, а не подставляет клиент.
   */
  async markPaid(id: string, inCash: boolean): Promise<SubscriptionResponse> {
    const { data } = await apiClient.post<SubscriptionResponse>(`/subscriptions/${id}/paid`, {
      inCash,
    });
    return data;
  },
};

/**
 * Сохраняет файл на диск пользователя. Ссылку добавляем в документ: Firefox
 * не срабатывает на click() у элемента вне дерева. Отзыв URL откладываем —
 * сразу после клика браузер успевает отменить начатое скачивание.
 */
function saveBlob(data: Blob, fileName: string): void {
  const url = URL.createObjectURL(data);
  const link = document.createElement('a');
  link.href = url;
  link.download = fileName;

  document.body.append(link);
  link.click();
  link.remove();

  window.setTimeout(() => URL.revokeObjectURL(url), 30_000);
}

export const contractsApi = {
  /**
   * Договор ученика в .docx. Скачиваем через клиент, а не ссылкой: файл
   * закрыт авторизацией, а заголовок в обычный <a href> не подставить.
   */
  async download(studentId: string, studentName: string): Promise<void> {
    const { data } = await apiClient.get<Blob>(`/contracts/student/${studentId}`, {
      responseType: 'blob',
    });

    saveBlob(data, `Договор — ${studentName}.docx`);
  },

  async template(): Promise<ContractTemplate> {
    const { data } = await apiClient.get<ContractTemplate>('/contracts/template');
    return data;
  },

  /** Текущий шаблон файлом — с него начинается правка в Word. */
  async downloadTemplate(): Promise<void> {
    const { data } = await apiClient.get<Blob>('/contracts/template/file', {
      responseType: 'blob',
    });

    saveBlob(data, 'Шаблон договора.docx');
  },

  async uploadTemplate(file: File): Promise<ContractTemplate> {
    const form = new FormData();
    form.append('file', file);

    const { data } = await apiClient.post<ContractTemplate>('/contracts/template', form, {
      transformRequest: [
        (payload: unknown, headers) => {
          headers.delete('Content-Type');
          return payload;
        },
      ],
    });

    return data;
  },

  async resetTemplate(): Promise<ContractTemplate> {
    const { data } = await apiClient.delete<ContractTemplate>('/contracts/template');
    return data;
  },
};

export const requisitesApi = {
  async get(): Promise<StudioRequisites> {
    const { data } = await apiClient.get<StudioRequisites>('/studio/requisites');
    return data;
  },

  async save(payload: SaveStudioRequisitesPayload): Promise<StudioRequisites> {
    const { data } = await apiClient.put<StudioRequisites>('/studio/requisites', payload);
    return data;
  },
};

export const emailsApi = {
  async list(): Promise<EmailOutboxEntry[]> {
    const { data } = await apiClient.get<EmailOutboxEntry[]>('/emails');
    return data;
  },

  /** Вернуть письмо в очередь после починки почты. */
  async retry(id: string): Promise<void> {
    await apiClient.post(`/emails/${id}/retry`);
  },
};

export const paymentsApi = {
  async list(filters: PaymentFilters): Promise<PaymentRow[]> {
    const { data } = await apiClient.get<PaymentRow[]>('/payments', { params: filters });
    return data;
  },
};

/**
 * Список групп уходит повторами `groupIds=a&groupIds=b`: индексы axios
 * (`groupIds[0]=`) ASP.NET к массиву не привязывает.
 */
const repeatedParams = { indexes: null } as const;

export const reportsApi = {
  async payments(filters: ReportFilters): Promise<PaymentsReport> {
    const { data } = await apiClient.get<PaymentsReport>('/reports/payments', {
      params: filters,
      paramsSerializer: repeatedParams,
    });
    return data;
  },

  /** Перекличка за расчётный месяц: кто заплатил, кто нет. */
  async month(filters: MonthReportFilters): Promise<MonthReport> {
    const { data } = await apiClient.get<MonthReport>('/reports/month', { params: filters });
    return data;
  },

  /** Тот же отчёт файлом. Имя собирает страница — оно попадает в «Загрузки». */
  async excel(filters: ReportFilters, fileName: string): Promise<void> {
    const { data } = await apiClient.get<Blob>('/reports/payments/excel', {
      params: filters,
      paramsSerializer: repeatedParams,
      responseType: 'blob',
    });

    saveBlob(data, fileName);
  },

  /** Оплаченные услуги. Основание одно — дата оплаты: месяца у услуги нет. */
  async services(filters: ServicesReportFilters): Promise<ServicesReport> {
    const { data } = await apiClient.get<ServicesReport>('/reports/services', {
      params: filters,
      paramsSerializer: repeatedParams,
    });
    return data;
  },

  async servicesExcel(filters: ServicesReportFilters, fileName: string): Promise<void> {
    const { data } = await apiClient.get<Blob>('/reports/services/excel', {
      params: filters,
      paramsSerializer: repeatedParams,
      responseType: 'blob',
    });

    saveBlob(data, fileName);
  },
};

export const servicesApi = {
  async list(includeInactive: boolean): Promise<ServiceResponse[]> {
    const { data } = await apiClient.get<ServiceResponse[]>('/services', {
      params: { includeInactive },
    });
    return data;
  },

  /** Плитки, которые видит родитель на странице оплаты. */
  async available(): Promise<ServiceTile[]> {
    const { data } = await apiClient.get<ServiceTile[]>('/services/available');
    return data;
  },

  /** Услуга по адресу из ссылки — открывается и у скрытой. */
  async bySlug(slug: string): Promise<ServiceTile> {
    const { data } = await apiClient.get<ServiceTile>(`/services/${slug}`);
    return data;
  },

  async create(payload: SaveServicePayload): Promise<ServiceResponse> {
    const { data } = await apiClient.post<ServiceResponse>('/services', payload);
    return data;
  },

  async update(id: string, payload: SaveServicePayload): Promise<ServiceResponse> {
    const { data } = await apiClient.put<ServiceResponse>(`/services/${id}`, payload);
    return data;
  },
};

export const serviceOrdersApi = {
  async my(): Promise<ServiceOrder[]> {
    const { data } = await apiClient.get<ServiceOrder[]>('/service-orders/my');
    return data;
  },

  async list(filters: ServiceOrderFilters): Promise<ServiceOrder[]> {
    const { data } = await apiClient.get<ServiceOrder[]>('/service-orders', { params: filters });
    return data;
  },

  async get(id: string): Promise<ServiceOrder> {
    const { data } = await apiClient.get<ServiceOrder>(`/service-orders/${id}`);
    return data;
  },

  async create(payload: CreateServiceOrderPayload): Promise<ServiceOrder> {
    const { data } = await apiClient.post<ServiceOrder>('/service-orders', payload);
    return data;
  },

  async pay(id: string): Promise<PaymentStart> {
    const { data } = await apiClient.post<PaymentStart>(`/service-orders/${id}/pay`);
    return data;
  },

  async cancel(id: string): Promise<ServiceOrder> {
    const { data } = await apiClient.post<ServiceOrder>(`/service-orders/${id}/cancel`);
    return data;
  },

  async markPaid(id: string, inCash: boolean): Promise<ServiceOrder> {
    const { data } = await apiClient.post<ServiceOrder>(`/service-orders/${id}/paid`, { inCash });
    return data;
  },
};

export const pagesApi = {
  async list(): Promise<ContentPageListItem[]> {
    const { data } = await apiClient.get<ContentPageListItem[]>('/pages');
    return data;
  },

  async get(slug: ContentPageSlug): Promise<ContentPage> {
    const { data } = await apiClient.get<ContentPage>(`/pages/${slug}`);
    return data;
  },

  async update(slug: ContentPageSlug, payload: SaveContentPagePayload): Promise<ContentPage> {
    const { data } = await apiClient.put<ContentPage>(`/pages/${slug}`, payload);
    return data;
  },
};
